import React, { useCallback } from "react"

import { runLlmGenerationTask } from "@/llm-generation/client-runtime"
import { LLM_GENERATION_TASK_IDS } from "@/llm-generation/protocol"
import { Card, CardContent } from "@coval/ui/card"
import { Input } from "@coval/ui/input"
import { Tooltip } from "@coval/ui/tooltip"
import { Info } from "lucide-react"

import { IVRTreeBuilder } from "@/components/ivr-flow/IVRTreeBuilder"
import type { IVRFlow } from "@/types/IVRFlow"
import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import { GenerateTestSetFromIVRButton } from "./GenerateTestSetFromIVRButton"
import { RequiredMarker } from "./RequiredMarker"

interface IvrFlowAdherenceConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export const IvrFlowAdherenceConfig: React.FC<IvrFlowAdherenceConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const metadata = metricRecordData.metric_metadata ?? {}
  const flowLabelId = React.useId()
  const flow = (metadata.ivr_flow as IVRFlow | undefined) ?? null
  const matchThreshold =
    typeof metadata.match_threshold === "number"
      ? metadata.match_threshold
      : 0.85

  const handleFlowChange = useCallback(
    (next: IVRFlow | null) => {
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          ivr_flow: next ?? undefined,
        },
      })
    },
    [metricRecordData, onChange],
  )

  const handleThresholdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value)
      if (Number.isNaN(value)) {
        return
      }
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          match_threshold: value,
        },
      })
    },
    [metricRecordData, onChange],
  )

  const generateIvrFlow = useCallback((prompt: string) => {
    return runLlmGenerationTask<IVRFlow>({
      taskId: LLM_GENERATION_TASK_IDS.IVR_FLOW_GENERATE,
      input: { prompt },
    })
  }, [])

  return (
    <div className="space-y-6">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES} htmlFor="ivr-match-threshold">
            Match threshold
          </label>
          <Tooltip content="Fuzzy ratio (0..1) above which an agent prompt counts as matching the expected IVR node prompt.">
            <Info className={INFO_ICON_STYLES} />
          </Tooltip>
        </div>
        <Input
          id="ivr-match-threshold"
          aria-label="Match threshold"
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={matchThreshold}
          onChange={handleThresholdChange}
          className="w-32"
        />
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span id={flowLabelId} className={LABEL_STYLES}>
            IVR flow
            <RequiredMarker />
          </span>
          <Tooltip content="Build the IVR tree the agent is expected to follow. Each node has a prompt; edges define caller inputs (DTMF, regex, or fuzzy keyword) and where they lead.">
            <Info className={INFO_ICON_STYLES} />
          </Tooltip>
        </div>
        <Card role="group" aria-labelledby={flowLabelId}>
          <CardContent className="p-4">
            <IVRTreeBuilder
              initialFlow={flow}
              onFlowChange={handleFlowChange}
              generateIvrFlow={generateIvrFlow}
            />
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <GenerateTestSetFromIVRButton
          flow={flow}
          contextLabel={metricRecordData.metric_display_name}
        />
      </div>
    </div>
  )
}
